import { ensureCsrfToken, refreshCsrfToken } from "@/lib/api/csrf";
import type { IdentifyResult } from "../types";
import { sanitizeUrl } from "./LoginFormReducer";

export interface ApiPostResult {
  readonly ok: boolean;
  readonly status: number;
  readonly data: unknown;
  readonly retryAfterHeader: string | null;
}

interface AuthPostRequest {
  readonly baseFetch: typeof fetch;
  readonly url: string;
  readonly body: unknown;
  readonly signal: AbortSignal | undefined;
  readonly token: string | undefined;
}

/** Narrow an unknown auth payload before reading envelope fields.
 * @example `if (isAuthRecord(payload)) payload["code"]` */
export function isAuthRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Send one auth form mutation and recover once from an exact CSRF rejection.
 * @example `await postAuthJson("/api/v2/auth/identify", { login })` */
export async function postAuthJson(
  url: string,
  body: unknown,
  signal?: AbortSignal,
  baseFetch: typeof fetch = fetch,
): Promise<ApiPostResult> {
  try {
    const token = await ensureCsrfToken(baseFetch);
    const request = { baseFetch, url, body, signal, token };
    const first = await sendAuthPost(request);
    if (!isCsrfInvalid(first)) return first;
    const refreshed = await refreshCsrfToken(baseFetch);
    if (!refreshed) return first;
    return sendAuthPost({ ...request, token: refreshed });
  } catch {
    return failedAuthPost();
  }
}

async function sendAuthPost(request: AuthPostRequest): Promise<ApiPostResult> {
  // Native browser fetch rejects a foreign method receiver. Keep the injected
  // function detached so Chromium invokes it with the platform contract.
  const baseFetch = request.baseFetch;
  const response = await baseFetch(
    request.url,
    authPostInit(request.body, request.signal, request.token),
  );
  const text = await response.text().catch(() => "");
  return {
    ok: response.ok,
    status: response.status,
    data: parseJsonOrText(text),
    retryAfterHeader: retryAfterHeaderFromResponse(response),
  };
}

function authPostInit(
  body: unknown,
  signal: AbortSignal | undefined,
  token: string | undefined,
): RequestInit {
  const headers = new Headers({ "Content-Type": "application/json" });
  if (token) headers.set("X-CSRF-Token", token);
  const init: RequestInit = {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    credentials: "include",
  };
  if (signal) init.signal = signal;
  return init;
}

function parseJsonOrText(text: string): unknown {
  if (!text.trim()) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { detail: text };
  }
}

function retryAfterHeaderFromResponse(response: Response): string | null {
  const maybeHeaders = (
    response as { headers?: { get?: (name: string) => string | null } }
  ).headers;
  return maybeHeaders?.get?.("Retry-After") ?? null;
}

function isCsrfInvalid(result: ApiPostResult): boolean {
  if (result.status !== 403 || !isAuthRecord(result.data)) return false;
  return result.data["code"] === "CSRF_INVALID";
}

function failedAuthPost(): ApiPostResult {
  return { ok: false, status: 0, data: null, retryAfterHeader: null };
}

function hasBackendIdentityStatus(value: Record<string, unknown>): boolean {
  return (
    typeof value["exists"] === "boolean" && Array.isArray(value["loginMethods"])
  );
}

function parseBackendIdentityStatus(
  value: Record<string, unknown>,
): IdentifyResult {
  return {
    hasActivePayment: value["hasActivePayment"] === true,
    existsInMatrix: value["existsInMatrix"] === true,
    isAdmin: false,
    requires2fa: value["requires2fa"] === true,
    checkoutUrl: "",
    createMatrixUrl: "",
    paymentLinkName: "",
    paymentLinkPriceCents: 0,
    paymentLinkUrl: "",
  };
}

/** Normalize current and legacy identify envelopes for the login reducer.
 * @example `parseIdentifyPayload({ exists: true, loginMethods: [] })` */
export function parseIdentifyPayload(raw: unknown): IdentifyResult {
  const value =
    isAuthRecord(raw) && isAuthRecord(raw["data"])
      ? raw["data"]
      : isAuthRecord(raw)
        ? raw
        : {};
  if (hasBackendIdentityStatus(value)) return parseBackendIdentityStatus(value);
  const paymentLink = isAuthRecord(value["paymentLink"])
    ? value["paymentLink"]
    : {};
  return legacyIdentifyResult(value, paymentLink);
}

function legacyIdentifyResult(
  value: Record<string, unknown>,
  paymentLink: Record<string, unknown>,
): IdentifyResult {
  return {
    hasActivePayment:
      value["hasActivePayment"] === true || value["hasPagfyPayment"] === true,
    existsInMatrix: value["existsInMatrix"] === true,
    isAdmin: value["isAdmin"] === true,
    requires2fa: value["requires2fa"] === true,
    checkoutUrl: safeTextUrl(value["checkoutUrl"]),
    createMatrixUrl: safeTextUrl(value["createMatrixUrl"]),
    paymentLinkName: textValue(paymentLink["name"]),
    paymentLinkPriceCents: numberValue(paymentLink["priceCents"]),
    paymentLinkUrl: safeTextUrl(paymentLink["url"]),
  };
}

function safeTextUrl(value: unknown): string {
  return sanitizeUrl(textValue(value));
}

function textValue(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function numberValue(value: unknown): number {
  return typeof value === "number" ? value : 0;
}
